// Exact solver for "cut this region into N identical pieces".
//
// Every piece is a connected set of k = |region| / N cells. We enumerate the possible pieces that contain the
// first cell of the region, and for each one try to tile the rest of the region with turned/flipped copies of it
// (always covering the first uncovered cell next, so no tiling is found twice).

import { byReading, boundsOf, cellKey, orientations, type Cell } from "./polyomino";

export interface SolveResult {
  /** each solution is a list of pieces, each piece a list of cell indexes into the sorted region */
  solutions: number[][][];
  /** false when the search stopped early (limit or time budget) */
  complete: boolean;
}

export function solveCongruent(
  region: readonly Cell[],
  n: number,
  opts: { limit?: number; budgetMs?: number } = {}
): SolveResult & { cells: Cell[] } {
  const limit = opts.limit ?? 50;
  const deadline = Date.now() + (opts.budgetMs ?? 2000);
  const cells = region.slice().sort(byReading);
  const m = cells.length;
  const result: SolveResult & { cells: Cell[] } = { solutions: [], complete: true, cells };
  if (m === 0 || m % n !== 0 || m > 31) return result;
  const k = m / n;

  const b = boundsOf(cells);
  const W = b.w;
  const grid = new Int16Array(W * b.h).fill(-1);
  cells.forEach(([x, y], i) => (grid[(y - b.minY) * W + (x - b.minX)] = i));
  const idxAt = (x: number, y: number): number =>
    x < b.minX || x > b.maxX || y < b.minY || y > b.maxY ? -1 : grid[(y - b.minY) * W + (x - b.minX)];
  const nbrs = cells.map(([x, y]) => [idxAt(x + 1, y), idxAt(x - 1, y), idxAt(x, y + 1), idxAt(x, y - 1)].filter((i) => i >= 0));

  // all connected k-subsets containing cell 0 (bit masks; m <= 31 keeps them positive)
  let level = new Set<number>([1]);
  for (let size = 1; size < k; size++) {
    const next = new Set<number>();
    for (const mask of level) {
      for (let i = 0; i < m; i++) {
        if (!(mask & (1 << i))) continue;
        for (const j of nbrs[i]) if (!(mask & (1 << j))) next.add(mask | (1 << j));
      }
    }
    level = next;
  }

  const full = m === 31 ? 0x7fffffff : (1 << m) - 1;
  let ticks = 0;
  let aborted = false;

  for (const first of level) {
    if (aborted) break;
    const pieceCells: Cell[] = [];
    for (let i = 0; i < m; i++) if (first & (1 << i)) pieceCells.push(cells[i]);
    // orientations as offsets from their first cell in reading order (orientations() sorts that way)
    const offsets = orientations(pieceCells).map((o) => o.map(([x, y]) => [x - o[0][0], y - o[0][1]] as Cell));

    const chosen: number[] = [first];
    const dfs = (covered: number): void => {
      if (aborted) return;
      if ((++ticks & 0x3fff) === 0 && Date.now() > deadline) {
        aborted = true;
        return;
      }
      if (covered === full) {
        result.solutions.push(chosen.map((mask) => maskToIndexes(mask, m)));
        if (result.solutions.length >= limit) aborted = true;
        return;
      }
      let u = 0;
      while (covered & (1 << u)) u++;
      const [ux, uy] = cells[u];
      for (const off of offsets) {
        let mask = 0;
        let ok = true;
        for (const [dx, dy] of off) {
          const j = idxAt(ux + dx, uy + dy);
          if (j < 0 || covered & (1 << j) || mask & (1 << j)) {
            ok = false;
            break;
          }
          mask |= 1 << j;
        }
        if (!ok) continue;
        chosen.push(mask);
        dfs(covered | mask);
        chosen.pop();
        if (aborted) return;
      }
    };
    dfs(first);
  }
  result.complete = !aborted;
  return result;
}

function maskToIndexes(mask: number, m: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < m; i++) if (mask & (1 << i)) out.push(i);
  return out;
}

/** Turn a solution into a piece index for every cell key. */
export function pieceMap(cells: readonly Cell[], solution: number[][]): Map<string, number> {
  const sorted = cells.slice().sort(byReading);
  const out = new Map<string, number>();
  solution.forEach((piece, p) => piece.forEach((i) => out.set(cellKey(sorted[i][0], sorted[i][1]), p)));
  return out;
}
